// ContentPanel — Tool result router
//
// Routes Anna's tool results to real renderers:
//   show_content_block → ContentBlock
//   show_exercise      → Exercise
// Filters out show_concept_map (rendered by ConceptMap in DialogLayout)
// and update_bloom_level (rendered by BloomTracker in SessionHeader).

import { useTranslation } from 'react-i18next';
import type { ToolResult } from '@hooks/useAnnaChat';
import ContentBlock from '../visualizations/ContentBlock';
import Exercise from '../visualizations/Exercise';

interface ContentPanelProps {
  toolResults: ToolResult[];
  accentColor: string;
  isStreaming?: boolean;
  onSubmitAnswer?: (answer: string) => void;
}

export default function ContentPanel({
  toolResults,
  accentColor,
  isStreaming,
  onSubmitAnswer,
}: ContentPanelProps) {
  const { t } = useTranslation('session');

  // Filter out tools handled elsewhere
  const visibleResults = toolResults.filter(
    (tr) =>
      tr.toolName !== 'update_bloom_level' &&
      tr.toolName !== 'show_concept_map',
  );

  if (visibleResults.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <p className="text-sm">
          {isStreaming
            ? t('contentPanel.waitingForContent')
            : t('contentPanel.contentShown')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {visibleResults.map((tr) => {
        const input = tr.input as Record<string, unknown>;

        if (tr.toolName === 'show_content_block') {
          const title = typeof input.title === 'string' ? input.title : '';
          const content = typeof input.content === 'string' ? input.content : '';
          const type = typeof input.type === 'string' ? input.type : 'definition';

          return (
            <ContentBlock
              key={tr.id}
              title={title}
              content={content}
              type={type as 'definition' | 'example' | 'analogy' | 'summary'}
              accentColor={accentColor}
            />
          );
        }

        if (tr.toolName === 'show_exercise') {
          const type = typeof input.type === 'string' ? input.type : 'explain_in_own_words';
          const question = typeof input.question === 'string' ? input.question : '';
          const options = Array.isArray(input.options) ? (input.options as string[]) : undefined;
          const correctAnswer = typeof input.correct_answer === 'string' ? input.correct_answer : undefined;
          const hint = typeof input.hint === 'string' ? input.hint : undefined;

          return (
            <Exercise
              key={tr.id}
              type={type as 'fill_in_blank' | 'multiple_choice' | 'comparison_table' | 'explain_in_own_words'}
              question={question}
              options={options}
              correctAnswer={correctAnswer}
              hint={hint}
              accentColor={accentColor}
              onSubmitAnswer={onSubmitAnswer}
            />
          );
        }

        // Unknown tool type — render basic fallback card
        return (
          <div
            key={tr.id}
            className="rounded-xl border border-gray-700 bg-gray-900/50 p-4 text-sm text-gray-400 animate-fade-in-simple"
          >
            <span className="text-xs uppercase tracking-wide">{tr.toolName}</span>
          </div>
        );
      })}
    </div>
  );
}
